import { useNavigate, useSearchParams } from 'react-router-dom';
import useRoles from '../../features/Roles/hooks/useRoles';
import { useEffect, useState, useRef } from 'react';
import {
  Button,
  Center,
  Flex,
  Heading,
  Box,
  Text,
  Spinner,
  TableContainer,
  Table as ChakraTable,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Badge,
  HStack,
  IconButton,
  useToast,
  Input,
  InputGroup,
  InputLeftElement
} from '@chakra-ui/react';
import { FaPlus, FaEdit, FaTrash, FaBolt, FaSearch, FaFileUpload } from 'react-icons/fa';
import { ROUTES } from '../../consts/routes';
import { OlaggModule, PermissionEnum, TriviaQuestionTemplate } from '@olagg/db-types';
import Pagination from '@olagg/ui-kit/Pagination';
import useTrivia from './hooks/useTrivia';

const TriviaQuestionsList = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { missingPermission } = useRoles();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getTriviaTemplates, deleteTriviaTemplate, bulkCreateTriviaTemplates, loading } = useTrivia();
  const [templates, setTemplates] = useState<TriviaQuestionTemplate[]>([]);
  const [count, setCount] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [filter, setFilter] = useState({
    page: Number(searchParams.get('page') || 1),
    per_page: 20,
    search: searchParams.get('search') || ''
  });

  const { page, per_page, search } = filter;

  const onFilter = ({ name, value }: { name: string; value: any }) => {
    const parsedValue = name === 'page' ? Number(value) : value;
    setFilter({ ...filter, [name]: parsedValue, ...(name !== 'page' && { page: 1 }) });

    if (value) searchParams.set(name, value);
    else searchParams.delete(name);
    setSearchParams(searchParams);
  };

  const onClearFilters = () => {
    setFilter({
      page: 1,
      per_page: 20,
      search: ''
    });
    setSearchParams(new URLSearchParams());
  };

  const refreshTemplates = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const fetchTemplates = async () => {
    try {
      const response = await getTriviaTemplates({
        page,
        per_page,
        search: search || undefined
      });
      setTemplates(response.triviaQuestionTemplates || []);
      setCount(response.meta?.count || 0);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [page, per_page, search, refreshTrigger]);

  const handleEditTemplate = (templateId: string) => {
    navigate(`/trivia/${templateId}`);
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (window.confirm('¿Estás seguro que deseas eliminar este template? Esto eliminará también todas sus asignaciones.')) {
      setDeletingId(templateId);
      try {
        await deleteTriviaTemplate(templateId);
        toast({
          title: 'Template eliminado',
          status: 'success',
          duration: 3000,
          isClosable: true
        });
        refreshTemplates();
      } catch (error: any) {
        toast({
          title: 'Error al eliminar',
          description: error?.message || 'No se pudo eliminar el template',
          status: 'error',
          duration: 5000,
          isClosable: true
        });
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleBulkUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await bulkCreateTriviaTemplates(file);
      toast({
        title: 'Templates creados',
        description: `Se crearon ${result.created_count || 'los'} templates exitosamente`,
        status: 'success',
        duration: 5000,
        isClosable: true
      });
      refreshTemplates();
    } catch (error: any) {
      toast({
        title: 'Error al importar',
        description: error?.message || error?.error || 'No se pudieron crear los templates',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <Center w='full' pb={4}>
      <Box color={'white'} w='full'>
        <Heading textAlign="center" mb={6} as="h1">
          Trivia - Catálogo de Templates
        </Heading>

        <Flex w='full' alignItems='center' gap='5' px={4}>
          <Button
            mt='2' mb='5'
            display='flex'
            variant="outlinedGradient"
            leftIcon={<FaPlus />}
            onClick={() => navigate(ROUTES.TRIVIA.create)}
            isDisabled={missingPermission(OlaggModule.Trivia, PermissionEnum.CREATE)}
          >
            Crear Template
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            accept=".xlsx,.xls"
            onChange={handleBulkUpload}
            style={{ display: 'none' }}
          />
          <Button
            mt='2' mb='5'
            display='flex'
            variant="outline"
            colorScheme="green"
            leftIcon={<FaFileUpload />}
            onClick={() => fileInputRef.current?.click()}
            isLoading={uploading}
            isDisabled={missingPermission(OlaggModule.Trivia, PermissionEnum.CREATE)}
          >
            Importar XLSX
          </Button>
        </Flex>
        {/* Search */}
        <Box mx={4} mb={6}>
          <InputGroup size="md">
            <InputLeftElement pointerEvents="none">
              <FaSearch color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Buscar templates..."
              value={search}
              onChange={(e) => onFilter({ name: 'search', value: e.target.value })}
              bg="gray.800"
              borderColor="gray.600"
              color="white"
              _placeholder={{ color: 'gray.400' }}
            />
          </InputGroup>
        </Box>

        {loading ? (
          <Box height='300px' display='flex' justifyContent='center' alignItems='center' fontWeight='600' flexDirection='column'>
            <Spinner />
          </Box>
        ) : (
          templates?.length > 0 ? (
            <Flex w={'full'} pb={4} justifyContent='center' flexDirection={'column'}>
              <Flex w='full' justifyContent='center' px={'20px'} pb={'15px'}>
                <TableContainer overflowX={'scroll'} overflowY={'scroll'} w="100%" maxH="60vh">
                  <ChakraTable
                    variant="striped"
                    size="sm"
                    colorScheme="olaggPurple"
                    fontSize="18px"
                    position='relative'
                    whiteSpace={'normal'}
                  >
                    <Thead>
                      <Tr position='sticky' bg='gray.800' top='0' zIndex='100'>
                        <Th py={'20px'} color="white">ID</Th>
                        <Th py={'20px'} color="white" minW="300px">Título</Th>
                        <Th py={'20px'} color="white">Puntos</Th>
                        <Th py={'20px'} color="white">Tiempo</Th>
                        <Th py={'20px'} color="white">Respuestas</Th>
                        <Th py={'20px'} color="white">Bonus</Th>
                        <Th py={'20px'} color="white">Asignaciones</Th>
                        <Th py={'20px'} color="white">Fechas Asignadas</Th>
                        <Th py={'20px'} color="white">Acciones</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {templates?.map((template) => (
                        <Tr key={template.id} color="white">
                          <Td py={'20px'}>
                            <Badge colorScheme="gray">{template.id}</Badge>
                          </Td>
                          <Td py={'20px'} maxW="400px">
                            <Text noOfLines={2}>{template.title}</Text>
                          </Td>
                          <Td py={'20px'}>{template.points}</Td>
                          <Td py={'20px'}>
                            {template.timeLimitSeconds
                              ? `${template.timeLimitSeconds}s`
                              : 'Sin límite'}
                          </Td>
                          <Td py={'20px'}>
                            <Badge colorScheme="blue">
                              {template.triviaTemplateAnswers?.length || 0}
                            </Badge>
                          </Td>
                          <Td py={'20px'}>
                            {template.speedBonusEnabled ? (
                              <Badge colorScheme="yellow">
                                <HStack spacing={1}>
                                  <FaBolt />
                                  <Text>x{template.speedBonusMultiplier}</Text>
                                </HStack>
                              </Badge>
                            ) : (
                              <Text color="gray.500">-</Text>
                            )}
                          </Td>
                          <Td py={'20px'}>
                            <Badge colorScheme="purple">
                              {template.usageCount || 0}
                            </Badge>
                          </Td>
                          <Td py={'20px'}>
                            {template.assignedDates && template.assignedDates.length > 0 ? (
                              <Text fontSize="xs" noOfLines={2}>
                                {template.assignedDates.slice(0, 3).join(', ')}
                                {template.assignedDates.length > 3 && ` +${template.assignedDates.length - 3} más`}
                              </Text>
                            ) : (
                              <Text color="gray.500" fontSize="xs">Sin asignar</Text>
                            )}
                          </Td>
                          <Td py={'20px'}>
                            <HStack spacing={2}>
                              <IconButton
                                aria-label="Editar"
                                icon={<FaEdit />}
                                size="sm"
                                colorScheme="blue"
                                onClick={() => handleEditTemplate(template.uid)}
                                isDisabled={missingPermission(OlaggModule.Trivia, PermissionEnum.EDIT)}
                              />
                              <IconButton
                                aria-label="Eliminar"
                                icon={<FaTrash />}
                                size="sm"
                                colorScheme="red"
                                onClick={() => handleDeleteTemplate(template.uid)}
                                isDisabled={missingPermission(OlaggModule.Trivia, PermissionEnum.DELETE)}
                                isLoading={deletingId === template.uid}
                              />
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </ChakraTable>
                </TableContainer>
              </Flex>
            </Flex>
          ) : (
            <Box height='300px' display='flex' justifyContent='center' alignItems='center' fontWeight='600' flexDirection='column'>
              <Text>No hay templates cargados</Text>
            </Box>
          )
        )}

        <Pagination
          params={{ limit: per_page, page, count }}
          onClick={page =>
            onFilter({
              name: 'page',
              value: String(page + 1)
            })
          }
          onLimitChange={val =>
            onFilter({
              name: 'per_page',
              value: val
            })
          }
          hideLimitInput
        />
      </Box>
    </Center>
  );
};

export default TriviaQuestionsList;
